/**
 * Imports required for the DateRangePicker component
 */
import { DateRangePicker as MUIDateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateRangeProps } from './props';
import { DateRange, LocalizationProvider } from '@mui/x-date-pickers-pro';
import styles from './styles.module.scss';
import { useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../hooks';
import getDateFNSLocale from '../../utils/i18n/getDateFNSLocale';
import getDatePickerTranslation from '../../utils/i18n/getDatePickerTranslation';

/**
 * DateRangePicker Component
 * A customized date range picker that wraps MUI's DateRangePicker with additional functionality
 *
 * @param {DateRangeProps} props - The component props
 * @param {DateRange<Date>} props.range - The selected date range
 * @param {Function} props.setRange - Function to update the date range
 * @param {boolean} props.isDefault - Flag to apply default styling
 * @param {boolean} props.isLoading - Flag to show loading state
 * @param {string} props.label - Custom label for the picker
 * @param {boolean} props.isSmall - Flag to apply small size styling
 * @returns {JSX.Element} Rendered DateRangePicker component
 */
export function DateRangePicker({
  range,
  setRange,
  isDefault,
  isLoading,
  label,
  isSmall,
}: DateRangeProps): JSX.Element {
  const { t, lang } = useTranslation('common');
  const locale = getDateFNSLocale(lang);
  const translation = getDatePickerTranslation(lang);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [tempRange, setTempRange] = useState<DateRange<Date>>(range);
  const tempRangeRef = useRef<DateRange<Date>>(range);

  /**
   * Removes the MUI X watermark from the DOM
   * Uses setTimeout to ensure removal after component renders
   */
  const deleteWatermark = () => {
    function remover() {
      const element = Array.from(document.querySelectorAll('div')).find(
        el => el.textContent == 'MUI X: Missing license key',
      );

      element?.remove();
      console.clear();
    }

    setTimeout(remover, 0);
    setTimeout(remover, 100);
    setTimeout(remover, 200);
  };

  /**
   * Updates the temporary date range state and ref
   * @param {DateRange<Date>} date - The new date range
   */
  function handleTempDate(date: DateRange<Date>) {
    setTempRange(date);
    tempRangeRef.current = date;
  }

  /**
   * Handles the date picker close event
   * Updates the final range with temporary range if available
   */
  function handleDateClose() {
    if (!tempRangeRef.current) {
      return;
    }

    setRange(tempRangeRef.current);
  }

  /**
   * Handles changes to the range prop
   * Updates temporary range if the new range is different from current temp range
   */
  function handleRangeChange() {
    if (!range) {
      return;
    }

    const [rangeStartDate, rangeEndDate] = range;
    const [tempStartDate, tempRangeEndDate] = tempRangeRef.current;

    const isWithChange: boolean =
      rangeStartDate?.getTime() !== tempStartDate?.getTime() ||
      rangeEndDate?.getTime() !== tempRangeEndDate?.getTime();

    if (!isWithChange) {
      return;
    }

    handleTempDate(range);
  }

  useEffect(handleRangeChange, [range]);

  // Render loading skeleton if isLoading is true
  if (isLoading) {
    return (
      <div
        className={`${styles.container} ${isDefault ? styles['container--default'] : ''}`}
      >
        <div className={styles['title-skeleton']} />
        <div className={styles.row}>
          <div className={styles['input-skeleton']} />
          <div className={styles['input-skeleton']} />
        </div>
      </div>
    );
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={locale}
      localeText={
        translation.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <MUIDateRangePicker
        value={tempRange}
        label={t('selector_placeholder')}
        onChange={handleTempDate}
        onClose={handleDateClose}
        disableFuture
        onOpen={deleteWatermark}
        renderInput={(startProps, endProps) => (
          <div
            className={`${styles.container} ${isDefault ? styles['container--default'] : ''} ${isSmall ? styles['container--small'] : ''}`}
            onClick={startProps.onClick}
          >
            <h6 className={styles['container__title']}>
              {label ? label : t('date_label')}:
            </h6>

            <div className={styles.row}>
              <input
                className={styles['date-input']}
                ref={startProps.inputRef as React.Ref<HTMLInputElement>}
                {...startProps.inputProps}
                placeholder={t('start_placeholder') as string}
                readOnly={isMobile}
              />

              <i className={`${styles.arrow} fa-solid fa-arrow-right`} />

              <input
                className={styles['date-input']}
                ref={endProps.inputRef as React.Ref<HTMLInputElement>}
                {...endProps.inputProps}
                placeholder={t('end_placeholder') as string}
                readOnly={isMobile}
              />
            </div>
          </div>
        )}
      />
    </LocalizationProvider>
  );
}
